import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { BehaviorSubject, ReplaySubject, interval, merge, of, takeUntil } from 'rxjs';
import { filter, switchMap, tap, throttle } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { ModalErrorComponent, ModalErrorData } from 'app/shared/modal-error/modal-error.component';
import { SnackBarType, SnackBarData, SnackBarComponent } from 'app/shared/snackbar/snackbar.component';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import User from 'entity/User';
import { ScreenShare, SocialState } from 'messages/websocket.messages';
import {
  EndRecordingMessage,
  OutgoingMessageType,
  ProcessRecordingMessage,
  RecordingType,
  StartRecordingMessage,
  UpdateType,
} from 'messages/websocket.messages';
import CompanyController from 'rest/CompanyController';
import RecordingController from 'rest/RecordingController';

import {
  ModalRecordingNameComponent,
  ModalRecordingNameData,
} from '../../modal-recording-name/modal-recording-name.component';
import { Player } from '../../social-pixi-canvas/player';
import SocialStateHelper from '../../social-state-machine/social-state-helper';
import SocialStateMachine from '../../social-state-machine/social-state-machine';
import { socialAppSelectors } from '../../state/app';
import { GROUP_SHARE_RECORDING_STOP_FAILED_ERROR, GROUP_SHARE_RECORDING_START_FAILED_ERROR } from '../../strings';
import { HeaderToastMessageService } from '../header-toast-message.service';
import { RegionService } from '../region/region.service';
import { SceneService } from '../scene.service';
import { GroupShareScreenParticipantService } from '../share-screen-group/group-share-screen-participant.service';
import { WebsocketService } from '../websocket.service';

import { MOVEMENT_INTERVAL } from './types';
import { createGroupRecordingId } from './util';

@Injectable({
  providedIn: 'root',
})

/**
 * Keep all recording logic for the group share screen recording components
 */
export class GroupShareScreenRecorderService extends Loggable implements OnDestroy {
  private store = inject<Store<AppState>>(Store);
  private headerToastMessageService = inject(HeaderToastMessageService);
  private dialog = inject(MatDialog);
  private websocketService = inject(WebsocketService);
  private groupShareParticipantService = inject(GroupShareScreenParticipantService);
  private sceneService = inject(SceneService);
  private regionService = inject(RegionService);
  private _snackBar = inject(MatSnackBar);

  public readonly isHost = signal(false);
  public isPresenter = false;

  public isGroupShareRecording = false;
  public isGroupShareRecording$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isUserPresentingAndRecording = false;

  public readonly isGroupShareRecordingEnabledFlag = signal(false);
  private isPublisherRecording = false;
  public readonly isRecordingDisabled = signal(false);
  private isUserSharing = false;

  public readonly groupSharingInProgress = signal(false);
  private groupPlayers: Player[] = [];

  public recordingId?: string;
  private socialId?: string;
  private social?: Social;
  private user?: User;
  private userId = 'unknown';

  private isWideRecording = false;

  // Group Share Screen
  public isEventActive = false;
  public readonly isUserSharingToGroup = signal(false);
  public readonly groupParticipantScreenShareActive = computed((): boolean => {
    return this.groupShareParticipantService.isGroupShareActiveSignal();
  });
  // public readonly isGroupShareActiveForParticipant = signal(false);
  public readonly screenSharingUserId = signal<string | undefined>(undefined);

  private groupShare?: ScreenShare;
  private groupShareIds?: string[];
  private group: Player[] = [];

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    super();
    this.setDisplayName('PresentationRecorderService');

    this.sceneService.primaryGroupMembers$
      .pipe(
        throttle(() => interval(MOVEMENT_INTERVAL), { leading: true }),
        takeUntil(this.destroyed$),
      )
      .subscribe((players) => {
        this.groupPlayers = players;
        // Handles player group for Recording state
        this.setGroupRecordingState();
      });

    // User state
    this.store.select(coreSelectors.getUserEntity).subscribe((user) => {
      this.user = user;
      this.userId = user?.userId ?? 'unknown';
    });

    // Social state
    this.store.select(socialAppSelectors.getCurrentSocial).subscribe((social) => {
      this.social = social;
    });
    this.store.select(socialAppSelectors.getCurrentSocialId).subscribe((socialId) => (this.socialId = socialId));
    this.store.select(socialAppSelectors.isHost).subscribe((isHost) => this.isHost.set(isHost));

    this.store
      .select(socialAppSelectors.getSocialSharedState)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((state: SocialState) => {
        this.isPresenter = SocialStateHelper.isUserPresenter(state, this.user?.userId ?? '');
      });

    this.store
      .select(socialAppSelectors.isUserSharingScreenToGroup)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserSharing) => {
        this.isUserSharing = isUserSharing;
      });

    // Group Screen Share
    this.groupShareParticipantService.groupShareId$.pipe(takeUntil(this.destroyed$)).subscribe((id) => {
      this.screenSharingUserId.set(id);
    });

    this.store
      .select(socialAppSelectors.getMyGroupScreenShare)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((screenShare) => {
        this.groupShare = screenShare;
        if (this.groupShare) {
          this.isGroupShareRecording = !!screenShare?.recordingId;
          this.isGroupShareRecording$.next(this.isGroupShareRecording);
          this.recordingId = screenShare?.recordingId;
        }
      });

    this.store
      .select(socialAppSelectors.getGroupShareIds)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((ids) => {
        this.groupShareIds = ids;
        this.handleGroupRecording();
        const userId = this.userId ?? '';
        if (this.groupShareIds.includes(userId)) {
          this.screenSharingUserId.set(userId);
        }
      });

    this.store
      .select(socialAppSelectors.isUserSharingScreenToGroup)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((active) => {
        this.isUserSharingToGroup.set(active);
      });

    // If no group share set recording to false
    if (!this.isUserSharingToGroup() && !this.groupParticipantScreenShareActive()) {
      this.isGroupShareRecording = false;
    }

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enable_group_screen_share_recording)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isRecordingEnabled) => this.isGroupShareRecordingEnabledFlag.set(isRecordingEnabled));

    this.store
      .select(socialAppSelectors.isWideRecording)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isWideRecording) => {
        this.isWideRecording = isWideRecording;
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  // -- Recorder Handlers --------------------------------------------------------------------------------------
  public handleRecordGroupShare(start = false): void {
    this.logUserAction(
      this.displayName,
      !this.isGroupShareRecording ? 'Start Recording Screen' : 'End Screen Recording',
    );
    // return if already recording
    if (this.isGroupShareRecording && start) {
      return;
    }
    // wait until this.screenSharingUserId is set
    if (!this.screenSharingUserId()) {
      setTimeout(() => this.handleRecordGroupShare(start), 1000);
      return;
    }
    this.toggleRecording();
  }

  public handleGroupRecording(): void {
    this.group.forEach((user) => {
      if (this.groupShareIds?.includes(user.userId)) {
        if (!this.groupShare) {
          return;
        }
        this.isGroupShareRecording = !!this.groupShare?.recordingId;
      }
    });
  }

  // Handle group logic for recording
  public setGroupRecordingState(): void {
    this.groupPlayers.forEach((user) => {
      if (this.groupShareIds?.includes(user.userId)) {
        // Set group share id for Host to start and end recording
        this.screenSharingUserId.set(user.userId);
        if (this.groupShare) {
          this.isGroupShareRecording = !!this.groupShare.recordingId;
          this.recordingId = this.groupShare.recordingId;
        }
      }
    });
  }

  // -- Group Share Recording --------------------------------------------------------------------------------------
  private get defaultRecordingName(): string {
    const socialName = this.social?.name ?? 'Room';
    return `${socialName} recording`;
  }

  // Check if user is sharing screen or if user is a participant in group share and host
  // Allows user to start recording
  public toggleRecording(): void {
    if (this.isWideRecording) {
      this.recordGroupShare(this.defaultRecordingName)
        .then(() =>
          this.openRecordingWarning('This Group screen share is being recorded.', 'success', 'START_RECORDING_3'),
        )
        .catch((error) => this.error("Couldn't start group share recording", error));
      return;
    }

    const enableRecording$ = of(this.isGroupShareRecording ?? false).pipe(
      filter((isRecording: boolean) => !isRecording),
      tap(() => {
        this.isRecordingDisabled.set(true);
        this.logUserAction(this.displayName, 'Start Recording');
      }),
      switchMap(() =>
        this.dialog
          .open<ModalRecordingNameComponent, ModalRecordingNameData, string>(ModalRecordingNameComponent, {
            data: { defaultName: this.defaultRecordingName },
          })
          .afterClosed(),
      ),
      // If the user cancels, clear the disable flag.
      tap((res) => {
        if (!res) {
          this.isRecordingDisabled.set(false);
        }
      }),
      filter(notNil),
      switchMap((name) => this.recordGroupShare(name)),
      tap(() => {
        this.openRecordingWarning('This Group screen share is being recorded.', 'success', 'START_RECORDING_3');
      }),
    );

    const disableRecording$ = of(this.isGroupShareRecording).pipe(
      filter((isRecording) => isRecording ?? false),
      tap(() => {
        this.isRecordingDisabled.set(true);
        this.logUserAction(this.displayName, 'Stop Group Share Recording');
      }),
      switchMap(() => this.endRecording()),
      tap(() => {
        this.headerToastMessageService.pushTemporaryMessage({
          message: 'Group Share Recording ended',
        });
      }),
    );

    merge(enableRecording$, disableRecording$).subscribe({
      next: () => {
        this.isRecordingDisabled.set(false);
      },
      error: (err: unknown) => {
        this.error("Couldn't toggle recording", err);
        const error = this.isGroupShareRecording
          ? GROUP_SHARE_RECORDING_STOP_FAILED_ERROR
          : GROUP_SHARE_RECORDING_START_FAILED_ERROR;
        this.dialog.open<ModalErrorComponent, ModalErrorData>(ModalErrorComponent, {
          data: error,
          panelClass: 'error-modal',
        });
        this.isRecordingDisabled.set(false);
      },
    });
  }

  private openRecordingWarning(message: string, type?: SnackBarType, icon?: string, duration = 6000) {
    const _data: SnackBarData = {
      message: message,
      type: type || '',
      icon: icon || '',
      mode: 'dark',
    };
    this._snackBar.openFromComponent(SnackBarComponent, {
      duration: duration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'custom-snackbar',
      data: _data,
    });
  }

  /**
   * Initiate a recording of the active presentation.
   * @param name The name of the recording.
   */
  public async recordGroupShare(name: string): Promise<void> {
    if (!this.groupSharingInProgress()) {
      this.error('recordGroupShare: Cannot record group share, screen is not active');
      throw new Error('Group Share is not active');
    }

    if (!this.social?.userId) {
      this.error('recordGroupShare: Cannot record presentation, social user Id undefined');
      throw new Error('socialUserId is undefined');
    }

    if (!this.screenSharingUserId()) {
      this.error('recordGroupShare: Cannot record presentation, screenSharingUserId is undefined');
      throw new Error('screenSharingUserId is undefined');
    }

    if (this.isGroupShareRecording) {
      this.error('recordGroup: Cannot record presentation, already recording');
      throw new Error('already recording');
    }

    const { companyId } = await new CompanyController().getUserCompany(this.social?.userId);
    if (!companyId) {
      this.error('Failed to retrieve company');
      throw new Error('Failed to retrieve company');
    }

    this.info('Recording presentation');

    if (!this.socialId) {
      throw new Error('socialId is undefined');
    }
    const recordingId = createGroupRecordingId({ socialId: this.socialId, companyId });

    const recording = await new RecordingController().processRecording({
      recordingId,
      socialId: this.socialId,
      name,
    });
    this.info('Recording created', { recording });

    if (this.screenSharingUserId()) {
      await SocialStateMachine.shared.requestUpdateGroupScreenShareRecording({
        groupShareId: this.screenSharingUserId()!,
        updatedProperties: { recordingId },
        updateType: UpdateType.CURRENT,
      });
      // If user is host and sharing screen start screen recording
      // Otherwise start recording for presenter of screen share
      if (this.isUserSharing) {
        const userId = this.user?.userId ?? '';
        this.startScreenRecording(userId);
      }
    }
  }

  public startScreenRecording(userId: string): void {
    if (!this.recordingId) {
      this.error('Attempted to start a group share recording without recording id.');
      return;
    }
    // Screen Recording
    this.info('Starting group share screen recording', { recordingId: this.recordingId });
    const screenMsg: StartRecordingMessage = {
      t: OutgoingMessageType.START_RECORDING,
      recordingId: this.recordingId,
      type: RecordingType.GROUP_SCREEN_SHARE,
      groupShareUserId: userId,
    };
    this.websocketService.sendMessage(screenMsg);

    if (this.isPublisherRecording) {
      return;
    }
    // Publisher Recording
    this.info('Starting group share publisher recording', { recordingId: this.recordingId });
    const pubMsg: StartRecordingMessage = {
      t: OutgoingMessageType.START_RECORDING,
      recordingId: this.recordingId,
      type: RecordingType.PUBLISHER,
      groupShareUserId: userId,
    };
    this.websocketService.sendMessage(pubMsg);
    this.isPublisherRecording = true;
  }

  public async endRecording(): Promise<boolean> {
    const { recordingId } = this;
    if (!this.isGroupShareRecording || !recordingId) {
      this.error('endRecording: Cannot end group share recording, recording is not active');
      return false;
    }

    this.isGroupShareRecording = false;
    this.info('Ending group share recording', { recordingId });

    if (this.screenSharingUserId()) {
      await SocialStateMachine.shared.requestUpdateGroupScreenShareRecording({
        groupShareId: this.screenSharingUserId()!,
        updatedProperties: { recordingId: undefined },
        updateType: UpdateType.CURRENT,
      });

      this.endScreenRecording();
    }

    // Process group share recording
    this.startRecordingProcessing(recordingId);

    this.recordingId = undefined;
    this.isPublisherRecording = false;

    return true;
  }

  // Finish screen sharing recording
  public endScreenRecording(): void {
    this.info('Stopping group share screen recording', { recordingId: this.recordingId });
    const screenMsg: EndRecordingMessage = {
      t: OutgoingMessageType.END_RECORDING,
      type: RecordingType.GROUP_SCREEN_SHARE,
      groupShareUserId: this.screenSharingUserId(),
    };
    this.websocketService.sendMessage(screenMsg);

    this.info('Stopping group share publisher recording', { recordingId: this.recordingId });
    const pubMsg: EndRecordingMessage = {
      t: OutgoingMessageType.END_RECORDING,
      type: RecordingType.PUBLISHER,
      groupShareUserId: this.screenSharingUserId(),
    };
    this.websocketService.sendMessage(pubMsg);
  }

  private startRecordingProcessing(recordingId: string) {
    this.info('Starting recording processing for Group share', { recordingId: this.recordingId });
    // Start Group Share processing
    const processingRecordingMessage: ProcessRecordingMessage = {
      t: OutgoingMessageType.PROCESS_RECORDING,
      recordingId,
    };

    this.websocketService.sendMessage(processingRecordingMessage);
  }

  //-- Visibility Validations --------------------------------------------------------------------------------------
  // Check if the group share is active.
  public get isGroupShareActive(): boolean {
    return (
      this.groupSharingInProgress() || this.isHost()
      // && this.isGroupShareActiveForParticipant()
    );
  }

  public setGroupSharingInProgress(isInProgress: boolean) {
    this.groupSharingInProgress.set(isInProgress);
  }

  // Check if region is available
  public readonly isRecordingOptionRegionEnabled = computed((): boolean => {
    return this.isGroupShareRecordingEnabledFlag() && this.regionService.isRecordingRegionAvailable;
  });

  // Check if the recording option is enabled
  public readonly isRecordingOptionEnabled = computed((): boolean => {
    return this.isGroupShareRecordingEnabledFlag() && !this.isRecordingDisabled();
  });

  // Check if the recording option can be showed
  public readonly showGroupShareRecordingOption = computed((): boolean => {
    return (
      this.isHost() &&
      this.isGroupShareRecordingEnabledFlag() &&
      (this.groupParticipantScreenShareActive() || this.isUserSharingToGroup())
    );
  });
}
